/**
 * Signed-in shell, matched to the live prototype's `.lms-header` and `.lms-footer`
 * (https://lms-prototype.mereka.dev/).
 *
 * Header composition is the prototype's, in its order:
 *   logo · nav · flexible spacer · actions
 * with nav labelled Dashboard / Courses / Learning Paths — not the wording I had
 * guessed — and actions holding Studio, Admin, settings, notifications, avatar.
 *
 * Actions appear by persona rather than for everyone: Studio and Admin are meaningless
 * to a learner, and showing disabled controls teaches people to ignore the toolbar.
 *
 * Signed-out visitors on inner pages get the same header with Sign in / Create account
 * instead of the account actions. The marketing landing page does NOT use this shell —
 * it carries its own transparent overlay nav.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import type { Persona, Session } from '../lib/session';
import { signOut } from '../lib/session';
import { getConfig, isEnabled, text } from '../lib/config';
import './shell.css';

const PERSONA_LABEL: Record<Persona, string> = {
  anonymous: '',
  learner: 'Learner',
  tenant_staff: 'Staff',
  tenant_admin: 'Admin',
  superadmin: 'Platform admin',
};

function Icon({ name }: { name: string }) {
  return (
    <span className="material-symbols-outlined" aria-hidden="true">
      {name}
    </span>
  );
}

export function Shell({ session, persona }: { session: Session; persona: Persona }) {
  const config = getConfig();
  const location = useLocation();
  const signedIn = session.authenticated;
  const isStaff = persona === 'tenant_staff' || persona === 'tenant_admin' || persona === 'superadmin';

  return (
    <div className="shell">
      <header className="lms-header">
        <Link to={signedIn ? '/dashboard' : '/'} className="lms-logo" aria-label={config.tenant.name}>
          {config.branding.logo ? (
            <img src={config.branding.logo} alt={config.tenant.name} height={26} />
          ) : (
            config.tenant.name
          )}
        </Link>

        <nav className="lms-nav" aria-label="Main">
          {signedIn && (
            <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              {text('nav_dashboard', 'Dashboard')}
            </NavLink>
          )}
          <NavLink to="/discover" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            {text('nav_courses', 'Courses')}
          </NavLink>
          {isEnabled('learning_paths') && (
            <NavLink to="/paths" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              {text('nav_paths', 'Learning Paths')}
            </NavLink>
          )}
        </nav>

        <div className="lms-header__spacer" />

        <div className="lms-header__actions">
          {signedIn ? (
            <>
              {isStaff && config.domains.studio && (
                <a
                  className="lms-admin-link"
                  href={`${window.location.protocol}//${config.domains.studio}`}
                >
                  <Icon name="edit_note" />
                  Studio
                </a>
              )}
              {isEnabled('notifications_drawer') && (
                <button type="button" className="icon-btn" aria-label="Notifications">
                  <Icon name="notifications" />
                </button>
              )}
              <Link to="/settings" className="icon-btn" aria-label="Settings">
                <Icon name="settings" />
              </Link>

              <AccountMenu session={session} persona={persona} />
            </>
          ) : (
            <>
              <Link
                to={`/login?next=${encodeURIComponent(location.pathname)}`}
                className="ui-btn"
                data-variant="ghost"
              >
                {text('sign_in', 'Sign in')}
              </Link>
              {config.auth.allowNativeRegistration && (
                <Link to="/register" className="ui-btn" data-variant="primary">
                  Create account
                </Link>
              )}
            </>
          )}
        </div>
      </header>

      <Outlet />

      <SiteFooter />
    </div>
  );
}

/** Avatar with the prototype's initials treatment, plus a compact account menu. */
function AccountMenu({ session, persona }: { session: Session; persona: Persona }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointer(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = session.user?.name || session.user?.username || '?';
  const initials = label
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
  const elevated = persona !== 'learner' && persona !== 'anonymous';
  const config = getConfig();

  /**
   * Where this person can switch to, highest authority last.
   *
   * Studio is a separate hostname rather than a route, so it is an <a>. The platform
   * console is a different host entirely and only exists for superusers.
   */
  const switchable: { to: string; label: string; hint: string; icon: string }[] = [];
  if (persona === 'tenant_staff' || persona === 'tenant_admin' || persona === 'superadmin') {
    switchable.push({
      to: '/admin',
      label: 'Admin dashboard',
      hint: `Manage ${config.tenant.name}`,
      icon: 'admin_panel_settings',
    });
  }
  if (persona === 'superadmin') {
    switchable.push({
      to: '/superadmin',
      label: 'Platform console',
      hint: 'All academies',
      icon: 'apartment',
    });
  }

  return (
    <div className="lms-account" ref={ref}>
      <button
        type="button"
        className="avatar"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account"
        onClick={() => setOpen(!open)}
      >
        {initials || '?'}
      </button>

      {open && (
        <div className="lms-menu" role="menu">
          <div className="lms-menu__head">
            <strong>{label}</strong>
            <span>{session.user?.email}</span>
            {elevated && <span className="lms-role">{PERSONA_LABEL[persona]}</span>}
          </div>
          <Link to="/u" role="menuitem" onClick={() => setOpen(false)}>
            Profile
          </Link>
          <Link to="/settings" role="menuitem" onClick={() => setOpen(false)}>
            Settings
          </Link>

          {/* Role switcher.
              Only the destinations this person actually holds appear — an "Admin"
              entry a learner cannot use is worse than no entry, and a disabled one
              invites support tickets. The separator and caption exist because
              switching authority is a different KIND of action from editing a
              profile, and a flat list of five links hides that. */}
          {switchable.length > 0 && (
            <>
              <span className="lms-menu__caption">Switch to</span>
              {switchable.map((target) => (
                <Link
                  key={target.to}
                  to={target.to}
                  role="menuitem"
                  className="lms-menu__switch"
                  onClick={() => setOpen(false)}
                >
                  <Icon name={target.icon} />
                  <span>
                    {target.label}
                    <small>{target.hint}</small>
                  </span>
                </Link>
              ))}
            </>
          )}

          <button type="button" role="menuitem" onClick={signOut}>
            {text('sign_out', 'Log out')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The prototype's dark common footer: three rows on #1a1623, 1280px container.
 *
 * Every link comes from the resolved tenant config — platform defaults from
 * config/platform.yaml, overridden per tenant in the database and edited from the
 * superadmin console. Nothing here is hardcoded.
 */
function SiteFooter() {
  const config = getConfig();
  const footer = config.footer ?? {};
  const columns = footer.columns ?? [];
  const social = footer.social ?? [];
  const legal = footer.legal ?? [];

  return (
    <footer className="lms-footer common-footer">
      <div className="footer-container">
        <div className="footer-row-1">
          <span className="footer-logo">{config.tenant.name}</span>
          {social.length > 0 && (
            <ul className="nav-socmed">
              {social.map((item) => (
                <li key={item.icon + item.href}>
                  <a href={item.href} aria-label={item.label ?? item.icon}>
                    <Icon name={item.icon} />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="footer-row-2">
          {columns.map((column) => (
            <div className="footer-column" key={column.heading}>
              <h4 className="footer-subheading">{column.heading}</h4>
              <ul className="nav-list">
                {column.links.map((link) => (
                  <li key={link.label + link.href}>
                    <a href={link.href}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="footer-row-3">
          <ul className="nav-legal">
            <li>
              &copy; {new Date().getFullYear()} {config.tenant.name}
            </li>
            {legal.map((link) => (
              <li key={link.label + link.href}>
                <a href={link.href}>{link.label}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
